import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import UhcCompatBadge from '../UhcCompatBadge';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
function ShoppingListForm({ onClose, onSave, fetchWithAuth, editingList, onOpenScan, onPrint }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [formData, setFormData] = useState({
    name: '',
    items: [],
    estimated_cost: '',
    store_preference: '',
    status: 'pending'
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);
  const [showPantryPicker, setShowPantryPicker] = useState(false);
  const [pantryItems, setPantryItems] = useState([]);
  const [pantryLoading, setPantryLoading] = useState(false);
  const [justAdded, setJustAdded] = useState('');

  useEffect(() => {
    if (!justAdded) return;
    const t = setTimeout(() => setJustAdded(''), 3500);
    return () => clearTimeout(t);
  }, [justAdded]);

  useEffect(() => {
    if (editingList) {
      setFormData({
        name: editingList.name || '',
        items: editingList.items || [],
        estimated_cost: editingList.estimated_cost || '',
        store_preference: editingList.store_preference || '',
        status: editingList.status || 'pending'
      });
    }
  }, [editingList]);

  const handleQuantityChange = (index, delta) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const currentQty = typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity) || 1;
          const newQty = Math.max(1, currentQty + delta);
          return { ...item, quantity: newQty };
        }
        return item;
      })
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Manual add — appends to the list and fetches the compatibility verdict so the badge shows
  // immediately (parity with Pick-from-Pantry). Shows a "✓ Added" confirmation as feedback.
  const handleAddManualItem = async () => {
    const name = newItemName.trim();
    if (!name) return;
    const qty = Math.max(1, parseInt(newItemQty) || 1);
    let uhc_compatibility;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/uhc-compat/check-item`, {
        method: 'POST',
        body: JSON.stringify({ name, kind: 'food' }),
      });
      if (res.ok) {
        const v = await res.json();
        if (v && v.severity && v.severity !== 'clear') {
          uhc_compatibility = { severity: v.severity, reasons: v.reasons || [], would_improve_with: v.would_improve_with || [] };
        }
      }
    } catch (error) {
      console.error('Compatibility check failed (item still added):', error);
    }
    setFormData(prev => ({ ...prev, items: [...prev.items, { name, quantity: qty, unit: '', uhc_compatibility }] }));
    setNewItemName('');
    setNewItemQty(1);
    setJustAdded(name);
  };

  // Pick from Pantry — load the user's primary-pantry items into a picker (restock path).
  const togglePantryPicker = async () => {
    if (showPantryPicker) { setShowPantryPicker(false); return; }
    setShowPantryPicker(true);
    setPantryLoading(true);
    try {
      const pr = await fetchWithAuth(`${API_BASE}/api/pantry/user/primary`);
      const pd = await pr.json();
      const pid = pd?.pantry?.id || pd?.id;
      if (!pid) { setPantryItems([]); return; }
      const ir = await fetchWithAuth(`${API_BASE}/api/pantry/${pid}/items?limit=200`);
      const id = await ir.json();
      setPantryItems(id.items || []);
    } catch (error) {
      console.error('Pantry load failed:', error);
      setPantryItems([]);
    } finally {
      setPantryLoading(false);
    }
  };

  const addPantryItemToCart = (pItem) => {
    const name = (pItem.name || '').trim();
    if (!name) return;
    setFormData(prev =>
      prev.items.some(i => (i.name || '').toLowerCase() === name.toLowerCase())
        ? prev
        : { ...prev, items: [...prev.items, { name, quantity: 1, unit: pItem.unit || '', uhc_compatibility: pItem.uhc_compatibility }] }
    );
  };

  const handlePrintList = () => {
    if (!editingList) return;
    if (onPrint) {
      onPrint(editingList);
    }
  };

  const handleVerifyList = async () => {
    if (!editingList?.id) return;

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/shopping-lists/${editingList.id}/status?new_status=verified`, {
        method: 'PATCH'
      });
      if (response.ok) {
        setFormData(prev => ({ ...prev, status: 'verified' }));
      }
    } catch (error) {
      console.error('Verify failed:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const url = editingList
        ? `${API_BASE}/api/shopping-lists/${editingList.id}`
        : `${API_BASE}/api/shopping-lists`;

      const method = editingList ? 'PUT' : 'POST';

      const submitData = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null
      };

      const response = await fetchWithAuth(url, {
        method: method,
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.detail || 'Failed to save shopping list');
      }
    } catch (error) {
      console.error('Failed to save shopping list:', error);
      setErrorMessage('Failed to save shopping list. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const STORE_OPTIONS = [
    'Walmart', 'Target', 'Costco', 'Kroger', 'Safeway',
    'Whole Foods', 'Trader Joe\'s', 'Aldi', 'Publix', 'Other'
  ];

  const getStatusDisplay = () => {
    const statusMap = {
      pending: { color: '#F59E0B', label: 'Pending' },
      verified: { color: '#1E3A5F', label: 'Verified' },
      printed: { color: '#3B82F6', label: 'Printed' },
      sent_to_instacart: { color: '#059669', label: 'Sent to Instacart' },
      fulfilled: { color: '#059669', label: 'Fulfilled' }
    };
    return statusMap[formData.status] || statusMap.pending;
  };

  const statusInfo = getStatusDisplay();

  return (
    <div className="modal-overlay">
      <div className="slc-modal">
        {/* Header */}
        <div className="slc-header">
          <h2>
            🛒 {editingList ? formData.name || 'Edit Cart' : 'Create Shopping Cart'}
          </h2>
          <button
            onClick={onClose}
            className="slc-closeBtn"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="slc-form">
          <div className="slc-content">
            {errorMessage && (
              <div className="slc-errorMessage">
                {errorMessage}
              </div>
            )}

            {/* Compact Header Row */}
            <div className="slc-fieldRow">
              <div className="flex-2">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                  placeholder="e.g., Weekly Groceries"
                  required
                  className="slc-input"
                />
              </div>
              <div className="flex-1">
                <label className="form-label">Status</label>
                <div className="slc-statusRow">
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: statusInfo.color }}></span>
                  <span style={{ color: statusInfo.color, fontSize: '13px', fontWeight: 500 }}>{statusInfo.label}</span>
                </div>
              </div>
            </div>

            {/* Add Items — manual entry + navy pill actions (D4S) */}
            {editingList && (
              <div className="slc-addSection">
                <div className="slc-addRow">
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddManualItem(); } }}
                    placeholder="Add an item (e.g., banana)"
                    className="slc-input slc-addNameInput"
                  />
                  <input
                    type="number"
                    min="1"
                    value={newItemQty}
                    onChange={(e) => setNewItemQty(e.target.value)}
                    className="slc-input slc-addQtyInput"
                    aria-label="Quantity"
                  />
                  <button type="button" onClick={handleAddManualItem} className="slc-pillBtn">
                    <span className="slc-pillIcon" aria-hidden="true">＋</span> Add
                  </button>
                </div>
                {justAdded && (
                  <div className="slc-addConfirm" role="status">✓ Added “{justAdded}” — it's at the bottom of your list</div>
                )}
                <div className="slc-pillRow">
                  <button
                    type="button"
                    onClick={() => onOpenScan && onOpenScan(editingList.id)}
                    className="slc-pillBtn"
                  >
                    <svg className="slc-pillIcon" width="17" height="14" viewBox="0 0 24 22" fill="currentColor" aria-hidden="true"><rect x="2" y="2" width="2.2" height="18"/><rect x="6" y="2" width="1.2" height="18"/><rect x="9" y="2" width="2.6" height="18"/><rect x="13.5" y="2" width="1.2" height="18"/><rect x="17" y="2" width="3" height="18"/><rect x="21.5" y="2" width="1.2" height="18"/></svg> Add by UPC
                  </button>
                  <button
                    type="button"
                    onClick={togglePantryPicker}
                    className="slc-pillBtn slc-pillBtn--ghost"
                  >
                    <span className="slc-pillIcon" aria-hidden="true">🥫</span> Pick from Pantry
                  </button>
                  <button
                    type="button"
                    onClick={handlePrintList}
                    className="slc-pillBtn slc-pillBtn--ghost"
                  >
                    <span className="slc-pillIcon" aria-hidden="true">🖨️</span> Print List
                  </button>
                </div>
                {showPantryPicker && (
                  <div className="slc-pantryPicker">
                    {pantryLoading ? (
                      <div className="slc-pantryEmpty">Loading pantry…</div>
                    ) : pantryItems.length === 0 ? (
                      <div className="slc-pantryEmpty">No pantry items found.</div>
                    ) : (
                      pantryItems.map(pi => {
                        const inCart = formData.items.some(i => (i.name || '').toLowerCase() === (pi.name || '').toLowerCase());
                        return (
                          <button
                            type="button"
                            key={pi.id}
                            disabled={inCart}
                            className={`slc-pantryPickItem${inCart ? ' slc-pantryPickItem--added' : ''}`}
                            onClick={() => addPantryItemToCart(pi)}
                          >
                            <span className="slc-pantryPickName">{pi.name}</span>
                            {pi.category && <span className="slc-pantryPickCat">{pi.category}</span>}
                            <span className="slc-pantryPickAdd">{inCart ? '✓ In cart' : '＋ Add'}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Store Row (Est. Cost removed — Instacart returns no final total; manual post-hoc $ had no value) */}
            <div className="slc-fieldRow">
              <div className="flex-1">
                <label className="form-label">Store</label>
                <select
                  value={formData.store_preference}
                  onChange={(e) => setFormData(prev => ({...prev, store_preference: e.target.value}))}
                  className="slc-select"
                >
                  <option value="">Select Store...</option>
                  {STORE_OPTIONS.map(store => (
                    <option key={store} value={store}>{store}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Divider */}
            <div className="slc-divider"></div>

            {/* Items Section */}
            <div className="slc-itemsHeader">
              <span className="slc-sectionLabel">Items</span>
              <span className="slc-itemCount">{formData.items.length} items</span>
            </div>

            {formData.items.length > 0 ? (
              <div className="slc-itemsList">
                {formData.items.map((item, idx) => {
                  const itemName = item.name || item.item_name || 'Unknown Item';
                  const currentQty = typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity) || 1;
                  const category = item.category || '';

                  return (
                    <div className="slc-itemGroup" key={idx}>
                    <div className="slc-itemRow">
                      <div className="flex-1 min-w-0">
                        <div className="slc-itemName">
                          {itemName}
                        </div>
                        <div className="slc-itemCategory">
                          {category}
                        </div>
                      </div>

                      {/* Quantity Controls */}
                      <div className="slc-qtyControls">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(idx, -1)}
                          className="slc-qtyBtn"
                        >
                          −
                        </button>
                        <span className="slc-qtyValue">
                          {currentQty}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(idx, 1)}
                          className="slc-qtyBtn"
                        >
                          +
                        </button>
                      </div>

                      {/* Delete Button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        className="slc-removeBtn"
                      >
                        ×
                      </button>
                    </div>
                    <UhcCompatBadge compatibility={item.uhc_compatibility} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="slc-emptyState">
                <div className="text-32 mb-8">🛒</div>
                <div>No items yet. Use "Scan Items" to add groceries.</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="slc-footer">
            <button
              type="button"
              onClick={onClose}
              className="slc-cancelBtn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="slc-submitBtn" style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.7 : 1 }}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ShoppingListForm;
